import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import {
  MapPin, Phone, Clock, User, ChefHat, Car, CheckCircle, Circle,
  Navigation, ChevronLeft, Flame, ShoppingBag, BadgeCheck, UserCheck,
  MessageSquare
} from 'lucide-react';
import { formatCurrencyXCD } from '../config/JadConfig';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import CustomerPickupScanSheet from '../components/CustomerPickupScanSheet';
import { normalizeStatus } from '../utils/orderStatus';

const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.head.appendChild(script);
  });
};

// Statuses may be stored 'Pending', 'In Delivery', 'inDelivery', 'picked_up'…
// — normalizeStatus (utils/orderStatus) handles all variants before comparing.

// iOS OrderStatusBadge/OrderHistoryRow palette (system colors).
const STATUS_STYLES = {
  pending:    { color: '#FF9500', label: 'Order Placed' },
  confirmed:  { color: '#007AFF', label: 'Confirmed' },
  preparing:  { color: '#FFCC00', label: 'Preparing' },
  ready:      { color: '#34C759', label: 'Ready for Pickup' },
  onmyway:    { color: '#AF52DE', label: 'On the Way' },
  indelivery: { color: '#AF52DE', label: 'In Delivery' },
  arrived:    { color: '#5856D6', label: 'Arrived' },
  delivered:  { color: '#34C759', label: 'Delivered' },
  completed:  { color: '#34C759', label: 'Completed' },
  cancelled:  { color: '#FF3B30', label: 'Cancelled' },
  pickedup:   { color: '#34C759', label: 'Picked Up' },
};

const getStatusInfo = (status) =>
  STATUS_STYLES[normalizeStatus(status)] || { color: '#8E8E93', label: status || 'Unknown' };

function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { db, user } = useFirebase();
  const { currentUser } = useAuth();
  const { clearCart, addToCart } = useCart();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDriverArrivedModal, setShowDriverArrivedModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPickupScanner, setShowPickupScanner] = useState(false);

  useEffect(() => {
    if (!orderId || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      (docSnap) => {
        if (!docSnap.exists()) {
          setError('Order not found');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        if (data.customerId !== user.uid) {
          setError('You do not have permission to view this order');
          setLoading(false);
          return;
        }

        const orderWithDate = {
          ...data,
          id: docSnap.id,
          createdAt: data.createdAt?.toDate() || new Date()
        };

        setOrder((prev) => {
          const becameArrived =
            normalizeStatus(orderWithDate.status) === 'arrived' &&
            normalizeStatus(prev?.status) !== 'arrived';
          if (becameArrived) setShowDriverArrivedModal(true);
          return orderWithDate;
        });
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading order:', err);
        setError(err.message || 'Failed to load order details');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId, user, db]);

  const statusKey = normalizeStatus(order?.status);
  // Firestore stores 'Pickup'/'Delivery' (capitalized) — normalize before
  // comparing or pickup orders render as delivery.
  const orderTypeKey = (order?.orderType || '').toLowerCase();
  const isDelivery = orderTypeKey === 'delivery';
  const isPickup = orderTypeKey === 'pickup';

  // iOS canTrack: delivery + confirmed/preparing/ready/inDelivery/onMyWay/arrived
  const canTrack = () =>
    isDelivery && ['confirmed', 'preparing', 'ready', 'onmyway', 'arrived', 'indelivery'].includes(statusKey);
  const canCancel = () => statusKey === 'pending';
  const canReorder = () => statusKey === 'completed' || statusKey === 'delivered';
  const canMarkCompleted = () => isPickup && statusKey === 'ready';
  const inDeliveryPhase = ['onmyway', 'arrived', 'indelivery'].includes(statusKey);

  const handleTrackOrder = () => {
    navigate(`/orders/${orderId}/track`);
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'cancelled',
        updatedAt: new Date()
      });
    } catch (err) {
      alert('Failed to cancel order: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkCompleted = async () => {
    setShowPickupScanner(false);
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'completed',
        updatedAt: new Date()
      });
    } catch (err) {
      alert('Failed to update order: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmDelivery = async () => {
    setIsUpdating(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'delivered',
        updatedAt: new Date()
      });
      setShowDriverArrivedModal(false);
      navigate('/orders');
    } catch (err) {
      alert('Failed to confirm delivery: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCallDriver = () => {
    if (order?.driverPhone) {
      window.location.href = `tel:${order.driverPhone}`;
    }
  };

  const handleCallRestaurant = () => {
    const phone = order?.restaurantPhone || '+18681234567';
    window.location.href = `tel:${phone}`;
  };

  const handleGetDirections = () => {
    const coords = order?.restaurantLocation?.coordinates;
    if (coords?.latitude && coords?.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${coords.latitude},${coords.longitude}`,
        '_blank'
      );
    } else {
      alert('Restaurant location not available');
    }
  };

  const handleReorder = () => {
    clearCart();
    (order.items || order.orderItems || []).forEach((item) => {
      const mi = item.menuItem || {};
      addToCart({
        id: item.menuItemId || item.id || mi.id,
        restaurantId: item.restaurantId || mi.restaurantId || order.restaurantId,
        name: item.menuItemName || item.name || mi.name,
        description: item.itemDescription || mi.description || 'Reordered item',
        price: item.price ?? mi.price ?? 0,
        menuItemImageURL: item.menuItemImageURL || mi.menuItemImageURL || '',
        category: 'Reorder',
        isAvailable: true,
        preparationTime: 15
      }, item.quantity || 1, null, item.variantName || null);
    });
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B30] mx-auto mb-4"></div>
          <p className="text-[#8E8E93]">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F2F2F7]">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-6 text-center">
          <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ChefHat size={32} className="text-[#FF3B30]" />
          </div>
          <h2 className="text-lg font-semibold mb-2">Error Loading Order</h2>
          <p className="text-[#8E8E93] text-sm mb-4">{error || 'Order not found'}</p>
          <button
            onClick={() => navigate('/orders')}
            className="w-full py-3 bg-[#FF3B30] text-white font-semibold rounded-xl"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const deliveryAddress = order.deliveryAddress;

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline nav bar: back chevron + centered "Order Details" */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center px-2 py-2 max-w-lg mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1 text-[#007AFF]"
            aria-label="Back"
          >
            <ChevronLeft size={26} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-gray-900">
            Order Details
          </h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-5 pb-10">
        {/* Driver Arrived banner — iOS green-bordered card + View Details */}
        {statusKey === 'arrived' && (
          <div className="rounded-xl p-4 bg-[#34C759]/10 border-2 border-[#34C759] space-y-3">
            <div className="flex items-center gap-3">
              <Car size={40} className="text-[#34C759] shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-[#34C759]">Driver Has Arrived!</h2>
                <p className="text-sm text-[#8E8E93]">Your order is ready for pickup</p>
              </div>
            </div>
            <button
              onClick={() => setShowDriverArrivedModal(true)}
              className="w-full py-3 bg-[#34C759] text-white font-semibold rounded-xl"
            >
              View Details
            </button>
          </div>
        )}

        {/* Order Status card — iOS orderStatusCard */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
          <h2 className="text-base font-semibold text-gray-900">Order Status</h2>

          <OrderProgressGauge order={order} statusKey={statusKey} />

          <div className="flex items-center justify-between">
            <p className="text-sm text-[#8E8E93]">
              {order.createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </p>
            <div className="flex items-center gap-2">
              {canCancel() && (
                <button
                  onClick={handleCancelOrder}
                  disabled={isUpdating}
                  className="px-3 py-2 bg-[#FF3B30] disabled:opacity-50 text-white text-sm rounded-full min-h-[32px]"
                >
                  Cancel
                </button>
              )}
              {canTrack() && (
                <button
                  onClick={handleTrackOrder}
                  className="px-3 py-2 bg-[#34C759] text-white text-sm rounded-full min-h-[32px] flex items-center gap-1"
                >
                  <Car size={15} />
                  Track
                </button>
              )}
              {canReorder() && (
                <button
                  onClick={handleReorder}
                  className="px-3 py-2 bg-[#34C759] text-white text-sm rounded-full min-h-[32px]"
                >
                  Order Again
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estimated Delivery — iOS etaCard */}
        {inDeliveryPhase && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-[#FF9500] shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Estimated Delivery</h3>
                <p className="text-sm text-[#8E8E93]">
                  {order.estimatedDeliveryTime
                    ? new Date(order.estimatedDeliveryTime?.toDate ? order.estimatedDeliveryTime.toDate() : order.estimatedDeliveryTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Calculating...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Locations map — iOS restaurantLocationMapView (delivery only) */}
        {isDelivery && (
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-gray-900">Locations</h3>
            <OrderMap order={order} />
          </div>
        )}

        {/* Order Items — iOS orderItemsCard */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h3 className="text-base font-semibold text-gray-900">Order Items</h3>

          <div className="divide-y divide-black/[0.06]">
            {(order.items || order.orderItems || []).map((item, index) => (
              <div key={item.id || item.menuItemId || index} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.menuItemName || item.name || item.menuItem?.name}{item.variantName ? ` (Size: ${item.variantName})` : ''}</p>
                  <p className="text-xs text-[#8E8E93]">Quantity: {item.quantity || 1}</p>
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {formatCurrencyXCD(item.totalPrice ?? (item.price ?? item.menuItem?.price ?? 0) * (item.quantity || 1))}
                </p>
              </div>
            ))}
          </div>

          {/* Order Summary — iOS orderSummarySection */}
          <div className="border-t border-black/[0.08] pt-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-900">Subtotal</span>
              <span>{formatCurrencyXCD(order.subtotal ?? order.totalAmount ?? 0)}</span>
            </div>
            {isDelivery && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-900">Delivery Fee</span>
                <span>{formatCurrencyXCD(order.deliveryFee || 0)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-900">Chef Service Fee (2%)</span>
              <span>{formatCurrencyXCD(order.chefServiceFee || 0)}</span>
            </div>
            {order.tipAmount > 0 && isDelivery && (
              <div className="flex justify-between text-sm">
                <span className="text-[#34C759]">Tip</span>
                <span className="text-[#34C759] font-semibold">{formatCurrencyXCD(order.tipAmount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="font-bold text-[#34C759]">Total</span>
              <span className="font-bold text-[#34C759]">{formatCurrencyXCD(order.totalAmount || 0)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Information — iOS customerInfoCard */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
          <h3 className="text-base font-semibold text-gray-900">
            {isDelivery ? 'Delivery Information' : 'Pickup Information'}
          </h3>

          <div className="flex items-center gap-2">
            <User size={16} className="text-[#34C759] shrink-0" />
            <span className="text-sm text-gray-900">{order.customerName}</span>
          </div>

          <div className="flex items-center gap-2">
            <Phone size={16} className="text-[#007AFF] shrink-0" />
            <span className="text-sm text-gray-900">{order.customerPhone || 'Phone not available'}</span>
          </div>

          {isDelivery && (
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-[#AF52DE] shrink-0 mt-0.5" />
              <div>
                {deliveryAddress ? (
                  <>
                    <p className="text-sm font-medium text-gray-900">{deliveryAddress.name}</p>
                    <p className="text-xs text-[#8E8E93]">{deliveryAddress.street}</p>
                    {deliveryAddress.apartment && (
                      <p className="text-xs text-[#8E8E93]">Apt: {deliveryAddress.apartment}</p>
                    )}
                    {deliveryAddress.parish && (
                      <p className="text-xs text-[#8E8E93]">{deliveryAddress.parish}</p>
                    )}
                    {deliveryAddress.island && (
                      <p className="text-xs text-[#8E8E93]">{deliveryAddress.island}</p>
                    )}
                    {deliveryAddress.landmark && (
                      <p className="text-xs text-[#8E8E93]">Near: {deliveryAddress.landmark}</p>
                    )}
                    {deliveryAddress.instructions && (
                      <p className="text-xs text-[#FF9500] italic">{deliveryAddress.instructions}</p>
                    )}
                  </>
                ) : order.customerAddress ? (
                  <>
                    <p className="text-sm text-gray-900">Delivery Address</p>
                    <p className="text-xs text-[#8E8E93]">{order.customerAddress}</p>
                  </>
                ) : (
                  <p className="text-xs text-[#8E8E93]">Address not available</p>
                )}
              </div>
            </div>
          )}

          {order.specialInstructions && (
            <div className="flex items-start gap-2">
              <MessageSquare size={16} className="text-[#FF9500] shrink-0 mt-0.5" />
              <p className="text-xs text-[#8E8E93]">{order.specialInstructions}</p>
            </div>
          )}
        </div>

        {/* Ready for Completion — iOS customer pickup actions */}
        {canMarkCompleted() && (
          <div className="bg-[#F2F2F7] border border-black/[0.06] rounded-xl p-4 space-y-3">
            <h3 className="text-base font-semibold text-[#34C759]">Ready for Pickup</h3>
            <p className="text-sm text-[#8E8E93]">
              Your order is ready! When you collect it, tap below and scan the QR code on your receipt to confirm.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleGetDirections}
                className="flex-1 py-3 px-4 bg-[#007AFF] text-white font-medium text-sm rounded-lg flex items-center justify-center gap-2"
              >
                <Navigation size={16} />
                Get Directions
              </button>
              <button
                onClick={() => setShowPickupScanner(true)}
                disabled={isUpdating}
                className="flex-1 py-3 px-4 bg-[#34C759] disabled:opacity-50 text-white font-medium text-sm rounded-lg flex items-center justify-center gap-2"
              >
                <CheckCircle size={16} />
                Mark as Picked Up
              </button>
            </div>
          </div>
        )}

        {/* Driver Status — iOS driver proximity card (delivery + driver assigned) */}
        {isDelivery && order.driverName && inDeliveryPhase && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-gray-900">Driver Status</h3>
                <div className="flex items-center gap-2">
                  <Car size={18} className="text-[#34C759]" />
                  <p className="text-sm text-[#8E8E93]">Driver {order.driverName} is en route</p>
                </div>
              </div>
              <button
                onClick={handleCallDriver}
                disabled={!order.driverPhone}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 ${
                  order.driverPhone ? 'bg-[#34C759]' : 'bg-[#8E8E93]'
                }`}
                aria-label="Call driver"
              >
                <Phone size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Call Restaurant — web convenience kept, styled as iOS red button */}
        <button
          onClick={handleCallRestaurant}
          className="w-full py-3 px-4 bg-[#FF3B30] text-white font-semibold rounded-xl flex items-center justify-center gap-2"
        >
          <Phone size={18} />
          Call Restaurant
        </button>
      </div>

      {/* Driver Arrived sheet — mirrors iOS DriverArrivedConfirmationView */}
      {showDriverArrivedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-6">
            <div className="text-center space-y-3">
              <Car size={48} className="text-[#34C759] mx-auto" />
              <h2 className="text-2xl font-bold text-gray-900">Driver Has Arrived!</h2>
              <p className="text-sm text-[#8E8E93]">
                Your order from {order.restaurantName} is ready for pickup.
              </p>
            </div>

            <div className="bg-[#F2F2F7] rounded-xl p-4 space-y-3">
              <h3 className="text-base font-semibold text-gray-900">Order Summary</h3>
              {(order.items || order.orderItems || []).slice(0, 3).map((item, i) => (
                <div key={item.id || i} className="flex justify-between text-sm">
                  <span className="text-gray-900">{item.menuItemName || item.name}{item.variantName ? ` (Size: ${item.variantName})` : ''}</span>
                  <span className="text-[#8E8E93]">{item.quantity}x</span>
                </div>
              ))}
              {(order.items || order.orderItems || []).length > 3 && (
                <p className="text-xs text-[#8E8E93]">... and {(order.items || order.orderItems || []).length - 3} more items</p>
              )}
              <div className="border-t border-black/[0.08] pt-3">
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-[#34C759]">{formatCurrencyXCD(order.totalAmount || 0)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleConfirmDelivery}
                disabled={isUpdating}
                className="w-full py-3 bg-[#34C759] disabled:opacity-50 text-white font-semibold rounded-xl"
              >
                Confirm Delivery
              </button>
              <button
                onClick={() => setShowDriverArrivedModal(false)}
                className="w-full py-3 bg-[#E5E5EA] text-[#8E8E93] font-medium rounded-xl"
              >
                Not Yet - Keep Waiting
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerPickupScanSheet
        open={showPickupScanner}
        onClose={() => setShowPickupScanner(false)}
        orderId={orderId}
        orderNumber={order.orderNumber || orderId}
        restaurantId={order.restaurantId}
        restaurantName={order.restaurantName}
        onVerified={handleMarkCompleted}
      />
    </div>
  );
}

// Mirrors iOS OrderProgressGauge: 8pt gradient progress bar + 32pt step
// circles with per-step SF-symbol-equivalent icons and system colors.
function OrderProgressGauge({ order, statusKey }) {
  const isPickup = (order.orderType || '').toLowerCase() === 'pickup';

  const steps = isPickup
    ? [
        { key: 'pending',   label: 'Order\nPlaced', Icon: Circle },
        { key: 'confirmed', label: 'Confirmed',     Icon: CheckCircle },
        { key: 'preparing', label: 'Preparing',     Icon: Flame },
        { key: 'ready',     label: 'Ready',         Icon: ShoppingBag },
        { key: 'completed', label: 'Completed',     Icon: CheckCircle },
      ]
    : [
        { key: 'pending',    label: 'Order\nPlaced', Icon: Circle },
        { key: 'confirmed',  label: 'Confirmed',     Icon: CheckCircle },
        { key: 'preparing',  label: 'Preparing',     Icon: Flame },
        { key: 'ready',      label: 'Ready',         Icon: ShoppingBag },
        { key: 'pickedup',   label: 'Picked\nUp',    Icon: BadgeCheck },
        { key: 'onmyway',    label: 'On the\nWay',   Icon: UserCheck },
        { key: 'arrived',    label: 'Arrived',       Icon: Car },
        { key: 'indelivery', label: 'In Delivery',   Icon: MapPin },
        { key: 'delivered',  label: 'Delivered',     Icon: CheckCircle },
      ];

  // iOS stepColors: orange, blue, yellow, green, mint, teal, purple, indigo, green
  const stepColors = isPickup
    ? ['#FF9500', '#007AFF', '#FFCC00', '#34C759', '#34C759']
    : ['#FF9500', '#007AFF', '#FFCC00', '#34C759', '#63E6E2', '#30B0C7', '#AF52DE', '#5856D6', '#34C759'];

  let currentIndex = steps.findIndex((s) => s.key === statusKey);
  if (currentIndex === -1) currentIndex = 0; // iOS firstIndex ?? 0

  return (
    <div className="space-y-4 py-2">
      {/* Progress bar */}
      <div className="relative h-2 bg-black/10 rounded-lg overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-lg transition-all duration-500"
          style={{
            width: `${((currentIndex + 1) / steps.length) * 100}%`,
            background: `linear-gradient(90deg, ${stepColors[currentIndex]} 0%, ${stepColors[Math.min(currentIndex + 1, stepColors.length - 1)]} 100%)`
          }}
        />
      </div>

      {/* Step indicators */}
      <div className="flex">
        {steps.map((step, index) => {
          const reached = index <= currentIndex;
          return (
            <div key={step.key} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: reached ? stepColors[index] : 'rgba(60,60,67,0.3)' }}
              >
                <step.Icon size={14} className={reached ? 'text-white' : 'text-[#8E8E93]'} />
              </div>
              <span
                className={`text-[10px] text-center leading-tight whitespace-pre-line ${
                  reached ? 'text-gray-900' : 'text-[#8E8E93]'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderMap({ order }) {
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const [mapLoading, setMapLoading] = useState(true);

  const restaurantCoords = order?.restaurantLocation?.coordinates;
  const deliveryCoords = order?.deliveryAddress?.coordinates;

  const hasRestaurant = restaurantCoords?.latitude && restaurantCoords?.longitude;
  const hasDelivery = deliveryCoords?.latitude && deliveryCoords?.longitude;

  useEffect(() => {
    if (!hasRestaurant || !mapRef.current) return;
    let cleanup = null;

    loadLeaflet()
      .then((L) => {
        const map = L.map(mapRef.current);

        const points = [
          [restaurantCoords.latitude, restaurantCoords.longitude]
        ];
        if (hasDelivery) {
          points.push([deliveryCoords.latitude, deliveryCoords.longitude]);
        }

        const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
        map.fitBounds(bounds, { padding: [30, 30] });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        const restaurantIcon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="width:28px;height:28px;border-radius:50%;background:#FF3B30;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.3);">🏪</div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28]
        });
        L.marker([restaurantCoords.latitude, restaurantCoords.longitude], { icon: restaurantIcon })
          .addTo(map)
          .bindPopup(order.restaurantName || 'Restaurant');

        if (hasDelivery) {
          const deliveryIcon = L.divIcon({
            className: 'custom-marker',
            html: `<div style="width:28px;height:28px;border-radius:50%;background:#007AFF;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.3);">🏠</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 28]
          });
          L.marker([deliveryCoords.latitude, deliveryCoords.longitude], { icon: deliveryIcon })
            .addTo(map)
            .bindPopup(order.deliveryAddress.name || 'Delivery');
        }

        mapInstanceRef.current = map;
        cleanup = () => map.remove();
        setMapLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load map:', err);
        setMapLoading(false);
      });

    return () => { if (cleanup) cleanup(); };
  }, [restaurantCoords?.latitude, restaurantCoords?.longitude, deliveryCoords?.latitude, deliveryCoords?.longitude]);

  if (!hasRestaurant) {
    return (
      <div className="bg-white rounded-xl h-[200px] flex items-center justify-center shadow-sm">
        <p className="text-[#8E8E93] text-sm">Location not available</p>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden h-[200px] bg-black/5 shadow-sm">
      {mapLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}

export default OrderDetailPage;
